import React, { useState } from "react";
import { TrendingUp, Menu, X, LogOut, User, Flame } from "lucide-react";
import AvatarPreview from "./AvatarPreview";
import { calculateMood, getMoodExpression } from "../lib/streakUtils";

const getLevelProgress = (xp) => {
  if (xp >= 5000) return { pct: 100, current: null, total: null, next: null };
  if (xp >= 2000) return { pct: ((xp - 2000) / 3000) * 100, current: xp - 2000, total: 3000, next: "Specialist" };
  if (xp >= 500)  return { pct: ((xp - 500)  / 1500) * 100, current: xp - 500,  total: 1500, next: "Resident" };
  return                 { pct: (xp / 500) * 100,            current: xp,        total: 500,  next: "Junior Doctor" };
};

const Header = ({ userData, onViewChange, onLogout }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const mood = calculateMood(userData.streakData);
  const moodExpression = getMoodExpression(mood);
  const progress = getLevelProgress(userData.xp || 0);

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={() => onViewChange("dashboard")}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <img src="/logo.png" alt="Logo" className="w-8 h-8" />
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">
              BioBlobs
            </h1>
          </button>

          {/* Desktop View - Full Stats */}
          <div className="hidden md:flex items-center gap-6">
            {/* Streak indicator */}
            <div className="flex items-center gap-1 bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-200">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="text-sm font-semibold text-orange-600">
                {userData.streakData?.currentStreak || 0}
              </span>
            </div>
            <div className="text-right">
              <div className="flex items-center justify-end gap-2 text-sm mb-1">
                <TrendingUp className="w-4 h-4 text-teal-600" />
                <span className="font-semibold text-gray-800">{userData.level}</span>
                <span className="text-gray-400">|</span>
                <span className="font-semibold text-yellow-600">{userData.coins} 🪙</span>
              </div>
              {progress.next ? (
                <>
                  <div className="w-36 h-1.5 bg-gray-200 rounded-full overflow-hidden ml-auto">
                    <div
                      className="h-full bg-gradient-to-r from-teal-400 to-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${progress.pct}%` }}
                    />
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    {progress.current} / {progress.total} XP → {progress.next}
                  </div>
                </>
              ) : (
                <div className="text-xs text-teal-600 font-semibold mt-0.5">Max Level ⭐</div>
              )}
            </div>
            <button
              onClick={() => onViewChange("avatar")}
              className="hover:opacity-80 transition-opacity"
            >
              <AvatarPreview avatar={userData.avatar} size="sm" moodOverride={moodExpression} />
            </button>
            <button
              onClick={onLogout}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Logout
            </button>
          </div>

          {/* Mobile View - Coins, Avatar, and Hamburger Menu */}
          <div className="flex md:hidden items-center gap-3">
            {/* Streak */}
            <div className="flex items-center gap-1 bg-orange-50 px-2 py-1 rounded-lg border border-orange-200">
              <Flame className="w-3 h-3 text-orange-500" />
              <span className="text-sm font-semibold text-orange-600">
                {userData.streakData?.currentStreak || 0}
              </span>
            </div>
            {/* Coins */}
            <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-lg border border-yellow-200">
              <span className="text-sm font-semibold text-yellow-600">
                {userData.coins}
              </span>
            </div>

            {/* Avatar */}
            <button
              onClick={() => onViewChange("avatar")}
              className="hover:opacity-80 transition-opacity"
            >
              <AvatarPreview avatar={userData.avatar} size="sm" moodOverride={moodExpression} />
            </button>

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {isMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-gray-200 space-y-3">
            {/* Streak */}
            <div className="flex items-center justify-between py-2 px-3 bg-orange-50 rounded-lg">
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="text-sm text-gray-600">Streak</span>
              </div>
              <span className="text-sm font-semibold text-orange-600">
                {userData.streakData?.currentStreak || 0} days
              </span>
            </div>

            {/* Level */}
            <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-600">Level</span>
              </div>
              <span className="text-sm font-semibold text-gray-800">
                {userData.level}
              </span>
            </div>

            {/* XP + level progress */}
            <div className="py-2 px-3 bg-teal-50 rounded-lg">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-teal-600" />
                  <span className="text-sm text-gray-600">XP</span>
                </div>
                <span className="text-sm font-semibold text-teal-600">{userData.xp}</span>
              </div>
              {progress.next ? (
                <>
                  <div className="h-1.5 bg-teal-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-400 to-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${progress.pct}%` }}
                    />
                  </div>
                  <div className="text-xs text-teal-600 mt-1">
                    {progress.current} / {progress.total} XP to {progress.next}
                  </div>
                </>
              ) : (
                <div className="text-xs text-teal-600 font-semibold">Max Level ⭐</div>
              )}
            </div>

            {/* Logout Button */}
            <button
              onClick={() => {
                setIsMenuOpen(false);
                onLogout();
              }}
              className="w-full flex items-center justify-center gap-2 py-2 px-3 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm font-semibold">Logout</span>
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
